<script setup lang="ts">
// The environments the caller sees: on their access list (and, for raw data, entitled to it), or every one for an
// admin. The API decides; scope and installation id filter on the server, search, status and data filter here.
import { computed, onBeforeUnmount, onMounted, ref } from 'vue';
import { must } from '../api/client';
import type { Environment, EnvironmentSummary } from '../api/types';
import { useContext } from '../context';
import { useSession } from '../stores/session';
import { classificationBadge, conditionSummary, environmentBuild, phaseBadge, formatTime, relativeTime, roleTone, type Tone } from '../status/status';
import StatusBadge from '../components/StatusBadge.vue';
import ProblemAlert from '../components/ProblemAlert.vue';
import Icon from '../components/Icon.vue';

const { api } = useContext();
const session = useSession();
const items = ref<EnvironmentSummary[]>([]);
const details = ref<Record<string, Environment>>({});
const error = ref<unknown>();
const loading = ref(true);
const loadedAt = ref<number>();
const scope = ref<'all' | 'mine' | 'shared'>('all');
const installationFilter = ref('');
const search = ref('');
const statusFilter = ref<'' | Tone>('');
const customerFilter = ref('');
let timer: number | undefined;

// The summary carries no conditions, so each row is completed from its detail, a few at a time.
async function enrich(names: string[]) {
  const queue = [...names];
  const worker = async () => {
    for (let n = queue.shift(); n; n = queue.shift()) {
      try {
        details.value[n] = must(await api.GET('/environments/{name}', { params: { path: { name: n } } }));
      } catch {
        /* the row keeps its summary */
      }
    }
  };
  await Promise.all([worker(), worker(), worker(), worker()]);
}

async function load() {
  try {
    if (!session.me) await session.load();
    const installationId = installationFilter.value.trim().toUpperCase() || undefined;
    const list = must(await api.GET('/environments', { params: { query: { scope: scope.value, installationId } } }));
    items.value = [...list.items].sort((a, b) => a.name.localeCompare(b.name));
    error.value = undefined;
    loadedAt.value = Date.now();
    void enrich(items.value.map((e) => e.name));
  } catch (e) {
    error.value = e;
  } finally {
    loading.value = false;
  }
}

onMounted(() => {
  void load();
  timer = window.setInterval(load, 15_000);
});
onBeforeUnmount(() => window.clearInterval(timer));

function setScope(s: 'all' | 'mine' | 'shared') {
  scope.value = s;
  loading.value = true;
  void load();
}
function reload() {
  loading.value = true;
  void load();
}

/** The data the listed environments hold, for the data filter. */
const customers = computed(() => [...new Set(items.value.map((e) => e.source.customer))].sort());

const all = computed(() =>
  items.value.map((e) => {
    const d = details.value[e.name];
    const merged = d ? { ...d, ...e } : e;
    const summary = conditionSummary(merged);
    return {
      env: e,
      phase: phaseBadge(e.phase),
      summary,
      tone: summary.tone === 'ok' ? phaseBadge(e.phase).tone : summary.tone,
      build: d ? environmentBuild(d) : '…',
      backup: d?.db?.backupId ?? e.backupId,
      classification: classificationBadge(d?.dataClassification),
      host: e.url?.replace(/^https?:\/\//, '').replace(/\/$/, ''),
    };
  }),
);

const rows = computed(() => {
  const q = search.value.trim().toLowerCase();
  return all.value.filter((r) => {
    if (customerFilter.value && r.env.source.customer !== customerFilter.value) return false;
    if (statusFilter.value && r.tone !== statusFilter.value) return false;
    if (!q) return true;
    return [r.env.name, r.env.installationId, r.env.owner, r.build, r.env.source.customer, r.host].some((v) => v?.toLowerCase().includes(q));
  });
});

const STATUS_OPTIONS: { value: '' | Tone; label: string }[] = [
  { value: '', label: 'Any' },
  { value: 'ok', label: 'Ready' },
  { value: 'progress', label: 'In progress' },
  { value: 'bad', label: 'Needs attention' },
  { value: 'idle', label: 'Stopped or paused' },
];
const statusCount = (t: '' | Tone) => (t ? all.value.filter((r) => r.tone === t).length : all.value.length);
const SCOPES = [
  { value: 'all', label: 'All I can see' },
  { value: 'mine', label: 'Mine' },
  { value: 'shared', label: 'Shared with me' },
] as const;
</script>

<template>
  <div class="page-head">
    <div class="grow">
      <h1>Environments</h1>
      <p class="muted">Environments you own or that are shared with you<template v-if="session.me?.admin">, and as an admin every other one</template>. For raw production data you also need the entitlement to that data.</p>
    </div>
    <RouterLink to="/new" class="button primary" data-testid="new-env"><Icon name="plus" /> New environment</RouterLink>
  </div>

  <ProblemAlert :error="error" context="Could not list environments" />
  <p v-if="session.me && !session.me.admin && !session.customers.length" class="notice" data-testid="no-entitlements">
    <strong>You have no data entitlements.</strong> You see the environments shared with you, but not the logs or actions of those with
    raw production data, and you cannot create one. Ask for a client role on <code>peak-backup</code> in Keycloak (e.g. <code>dev</code>).
  </p>

  <nav class="tabs" aria-label="Which environments">
    <button v-for="s in SCOPES" :key="s.value" type="button" class="tab" :aria-selected="scope === s.value" :data-testid="`scope-${s.value}`" @click="setScope(s.value)">
      {{ s.value === 'all' && session.me?.admin ? 'All (admin)' : s.label }}<span v-if="scope === s.value && !loading" class="count">{{ items.length }}</span>
    </button>
  </nav>

  <div class="inline" style="gap: 0.75rem">
    <label style="position: relative; flex: 1 1 260px; max-width: 380px; font-weight: 400">
      <span class="sr-only">Search environments</span>
      <span style="position: absolute; left: 12px; top: 11px; color: var(--muted)"><Icon name="search" /></span>
      <input v-model="search" type="search" placeholder="Name, installation, owner, build" style="padding-left: 36px" data-testid="filter-search" />
    </label>
    <label class="inline muted" style="font-weight: 400">
      Status
      <select v-model="statusFilter" style="width: auto" data-testid="filter-status">
        <option v-for="o in STATUS_OPTIONS" :key="o.value" :value="o.value">{{ o.label }} ({{ statusCount(o.value) }})</option>
      </select>
    </label>
    <label v-if="customers.length > 1" class="inline muted" style="font-weight: 400" for="filter-customer">
      Data
      <select id="filter-customer" v-model="customerFilter" style="width: auto">
        <option value="">any customer ({{ customers.length }})</option>
        <option v-for="c in customers" :key="c" :value="c">{{ c }}</option>
      </select>
    </label>
    <label class="inline muted" style="font-weight: 400" for="filter-installation">
      Installation
      <input id="filter-installation" v-model="installationFilter" placeholder="SKISUP-22329" style="width: 10rem" data-testid="filter-installation" @change="reload" />
    </label>
    <span class="inline muted" style="margin-left: auto; font-size: 12.5px"><span class="dot" style="color: var(--ok-dot)"></span> Live · refreshed every 15 s</span>
  </div>

  <p v-if="loading && !items.length" aria-live="polite">Loading…</p>
  <p v-else-if="!error && !rows.length" class="info">
    No environments<template v-if="scope === 'mine'"> owned by you</template><template v-else-if="scope === 'shared'"> shared with
    you</template><template v-if="installationFilter.trim()"> for installation {{ installationFilter.trim().toUpperCase() }}</template><template v-if="search.trim() || statusFilter || customerFilter"> match the filters</template>.
    You see an environment when you are on its access list.
  </p>

  <div v-if="rows.length" class="table-box">
    <div class="scroll">
      <table data-testid="env-table">
        <caption class="sr-only">{{ rows.length }} environment{{ rows.length === 1 ? '' : 's' }}, refreshed every 15 s</caption>
        <thead>
          <tr>
            <th scope="col">Environment</th>
            <th scope="col">Status</th>
            <th scope="col">Data source</th>
            <th scope="col">Build</th>
            <th scope="col">Access</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="r in rows" :key="r.env.name">
            <th scope="row">
              <RouterLink :to="{ name: 'detail', params: { name: r.env.name } }" style="font-size: 15px">{{ r.env.name }}</RouterLink>
              <div v-if="r.host" class="sub" style="font-weight: 400"><a :href="r.env.url" target="_blank" rel="noopener">{{ r.host }}</a></div>
              <div class="sub mono" style="font-weight: 400">{{ r.env.installationId ?? 'no installation id' }}</div>
            </th>
            <td>
              <StatusBadge :badge="r.phase" />
              <div class="sub"><span :style="r.summary.tone === 'bad' ? 'color: var(--bad-fg)' : ''" :title="r.summary.title">{{ r.summary.label }}</span></div>
            </td>
            <td>
              <div class="inline" style="gap: 6px"><span class="mono">{{ r.env.source.customer }} / {{ r.env.source.environment }}</span><span v-if="r.classification" class="pill sm" :class="`tone-${r.classification.tone}`" :title="r.classification.title">{{ r.classification.label }}</span></div>
              <div class="sub mono">{{ r.env.flavour ?? '—' }}</div>
              <div v-if="r.backup" class="sub mono" :title="r.backup">{{ r.backup.length > 30 ? r.backup.slice(0, 30) + '…' : r.backup }}</div>
            </td>
            <td class="mono">{{ r.build }}</td>
            <td>
              <span v-if="r.env.yourRole" class="pill sm" :class="roleTone(r.env.yourRole)" data-testid="your-role">you: {{ r.env.yourRole }}</span>
              <div class="sub">owner {{ r.env.owner ?? 'none (admin assigns)' }}</div>
              <div class="sub" :title="formatTime(r.env.createdAt)">created {{ relativeTime(r.env.createdAt) }}</div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
    <div class="foot">
      <span>{{ rows.length }} of {{ items.length }} environment{{ items.length === 1 ? '' : 's' }}</span>
      <span v-if="loadedAt">updated {{ relativeTime(new Date(loadedAt).toISOString()) }}</span>
    </div>
  </div>
</template>
